import React, { useEffect } from 'react';
import { useSSR } from 'react-i18next';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import 'apps/auth/components/i18n';
import 'static/css/fonts.css';
import 'static/css/main_auth.css';
import 'static/css/global.css';
import 'static/css/theme.css';
import 'apps/auth/theme/colors.css';
import 'apps/auth/theme/rebranding.css';
import 'static/css/auth-common.css';
import 'static/css/main_fix_auth.css';
import 'static/css/braze_content_cards.css';
import { ThemeProvider, StyleSheetManager } from 'styled-components';

import 'regenerator-runtime/runtime.js';
import isPropValid from '@emotion/is-prop-valid';
import * as Sentry from '@sentry/react';
import { trackPageTranslation } from 'apps/auth/analytics/pageTranslation';
import AuthApp from 'apps/auth/components/AuthApp';
import { CloudFlareError } from 'apps/auth/components/exceptions/CloudFlareError';
import { ForbiddenError } from 'apps/auth/components/exceptions/ForbiddenError';
import { MiddlewareError } from 'apps/auth/components/exceptions/MiddlewareError';
import ErrorBoundary from 'apps/auth/components/utils/ErrorBoundary';
import FlagsProvider from 'apps/auth/firebase/FlagProvider';
import { useThemeDetector } from 'apps/auth/hooks/theme';
import getStore from 'apps/auth/redux/store';
import routes from 'apps/auth/routes';
import { pushPath } from 'apps/auth/routes/routesUtils';
import pallets from 'apps/auth/theme/pallets';
import { hydrateRoot } from 'react-dom/client';

const preloadedState = window.__INITIAL_STATE__;
delete window.__INITIAL_STATE__;
const store = getStore(preloadedState);
const state = store.getState();
const { ballId, countryId, phone } = state.auth;
const errorCode = state.app?.messageBody?.[0]?.errorCode || null;

const preprocessRoutes = pushPath(routes, []);

const regExpWhiteListBrowsers =
    /Edge?\/(12[4-9]|1[3-9]\d|[2-9]\d{2}|\d{4,})\.\d+(\.\d+|)|Firefox\/(1{2}[5-9]|1[2-9]\d|[2-9]\d{2}|\d{4,})\.\d+(\.\d+|)|Chrom(ium|e)\/(109|1[1-9]\d|[2-9]\d{2}|\d{4,})\.\d+(\.\d+|)|(Maci|X1{2}).+ Version\/(17\.([4-9]|\d{2,})|(1[89]|[2-9]\d|\d{3,})\.\d+)([,.]\d+|)( \(\w+\)|)( Mobile\/\w+|) Safari\/|Chrome.+OPR\/(109|1[1-9]\d|[2-9]\d{2}|\d{4,})\.\d+\.\d+|(CPU[ +]OS|iPhone[ +]OS|CPU[ +]iPhone|CPU IPhone OS|CPU iPad OS)[ +]+(15[._]([6-9]|\d{2,})|(1[6-9]|[2-9]\d|\d{3,})[._]\d+)([._]\d+|)|Android:?[ /-](12[5-9]|1[3-9]\d|[2-9]\d{2}|\d{4,})(\.\d+|)(\.\d+|)|Mobile Safari.+OPR\/([89]\d|\d{3,})\.\d+\.\d+|Android.+Chrom(ium|e)\/(12[5-9]|1[3-9]\d|[2-9]\d{2}|\d{4,})\.\d+(\.\d+|)|Android.+(UC? ?Browser|UCWEB|U3)[ /]?(15\.([5-9]|\d{2,})|(1[6-9]|[2-9]\d|\d{3,})\.\d+)\.\d+|SamsungBrowser\/(2[5-9]|[3-9]\d|\d{3,})\.\d+/;

const isSupportedBrowser = () => (typeof window !== 'undefined' ? regExpWhiteListBrowsers.test(window.navigator.userAgent) : false);
const isSupported = () => isSupportedBrowser() && Boolean(window.IntersectionObserver);

let hasSentryInit = false;
let hasReplayInit = false;

const isAbortError = (err) => {
    if (!err) return false;
    const name = err.name || '';
    const code = err.code;
    const msg = typeof err.message === 'string' ? err.message.toLowerCase() : '';

    const isDOMAbort = typeof DOMException !== 'undefined' && err instanceof DOMException && name === 'AbortError';

    return (
        isDOMAbort ||
        name === 'AbortError' || // стандартное имя
        code === 20 || // legacy WebKit
        code === 'ABORT_ERR' || // строковый код в некоторых либах
        msg.includes('aborted') ||
        msg.includes('abort')
    );
};

const initSentry = () => {
    if (hasSentryInit) {
        return;
    }

    const path = __isProd__
        ? 'https://97323c0e4785467d89705570b372dc82@sentryg2.paysend.io/33'
        : 'https://c944137225b2f4f75e5fdc16e085a34a@sentryg2-dev.paysend.io/25';

    Sentry.init({
        dsn: path,
        integrations: [new Sentry.BrowserTracing()],
        tracesSampleRate: __isProd__ ? 0.8 : 1.0,
        environment: __isProd__ ? 'PRODUCTION' : 'TEST',
        maxBreadcrumbs: 20,
        autoSessionTracking: true,
        debug: false, //__isDev__ || __isTest__
        attachStacktrace: !__isProd__,
        release: 'paysend@' + __packageVersion__,
        replaysOnErrorSampleRate: 1.0,
        replaysSessionSampleRate: __isProd__ ? 0.8 : 1.0,
        beforeSend(event, hint) {
            const err = hint?.originalException ?? hint?.syntheticException;
            if (isAbortError(err)) return null; // глушим отменённые ошибки
            return event;
        },
        initialScope: {
            tags: { error_code: errorCode },
            user: { ball_id: ballId || '', country_id: countryId || '', phone: phone || '' },
        },
    });

    Sentry.addEventProcessor((event, hint) => {
        const error = hint?.originalException;
        if (error instanceof CloudFlareError) {
            event.level = 'warning';
        }
        if (error instanceof ForbiddenError) {
            event.level = 'log';
        }
        if (error instanceof MiddlewareError) {
            event.level = 'error';
        }
        if (isAbortError(error)) {
            event.level = 'info';
        }
        if (!isSupported()) {
            event.level = 'info';
        }
        return event;
    });

    hasSentryInit = true;
};

const initReplay = () => {
    if (hasReplayInit) {
        return;
    }

    const client = Sentry.getCurrentHub().getClient();
    const replay = new Sentry.Replay();
    // only reason client doesn't exist is that `Sentry.init` was not called.
    if (client && client.addIntegration) {
        client.addIntegration(replay);
        hasReplayInit = true;
    }
};

initSentry();

const IndexHydrate = () => {
    useEffect(() => {
        initReplay();
        const uh = (e) => {
            if (isAbortError(e.reason)) e.preventDefault();
        };
        window.addEventListener('unhandledrejection', uh);
        const stopTrackingTranslation = trackPageTranslation();
        return () => {
            window.removeEventListener('unhandledrejection', uh);
            stopTrackingTranslation();
        };
    }, []);
    useSSR(window.initialI18nStore, window.initialLanguage);

    const { theme } = useThemeDetector();

    useEffect(() => {
        document.documentElement.dataset.theme = theme;
    }, [theme]);

    return (
        <BrowserRouter>
            <StyleSheetManager shouldForwardProp={isPropValid}>
                <ThemeProvider theme={pallets[theme]}>
                    <Provider store={store}>
                        <FlagsProvider
                            defaults={{
                                show_currencies_in_countries_list: '1',
                            }}
                        >
                            <ErrorBoundary>
                                <AuthApp authUser={state.auth.authUser} redirectTo={state.app.redirectTo} routes={preprocessRoutes} />
                            </ErrorBoundary>
                        </FlagsProvider>
                    </Provider>
                </ThemeProvider>
            </StyleSheetManager>
        </BrowserRouter>
    );
};
const container = document.getElementById('app');
hydrateRoot(container, <IndexHydrate />);
